/* eslint-disable prefer-template */
import React from 'react'
import type { HTMLElementProps } from '~/types'
import { cn } from '~/utils'

interface BaseProps {
	active?: boolean
	secondary?: boolean
	transparent?: boolean
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
	disabled?: boolean
	onDisabledClick?: HTMLElementProps<'button'>['onClick']
}

type BtnProps = HTMLElementProps<'button', BaseProps & { href?: undefined }>
type AnchorProps = HTMLElementProps<'a', BaseProps & { href: string }>
export type ButtonProps = BtnProps | AnchorProps

export const Button: React.FC<ButtonProps> = props => {
	const { active, secondary, transparent, size, className, children, disabled, onDisabledClick, onClick, ...rest } =
		props
	const Elem = rest.href ? 'a' : 'button'
	return (
		<Elem
			className={cn(
				'button select-none whitespace-nowrap rounded-full border-solid no-underline',
				secondary
					? cn(
							'border-2 border-green-600 bg-white text-green-600',
							!disabled && 'hocus:text-green-500 hocus:border-green-500'
					  )
					: ' bg-green hocus:bg-green-300 border-0 text-white',
				transparent && 'hocus:bg-gray-800 hocus:bg-opacity-5 bg-transparent text-inherit',
				disabled && 'opacity-50 saturate-[0.8]',
				getSizeClassnames(size),
				className,
				{ active }
			)}
			aria-disabled={disabled}
			onClick={e => (disabled ? onDisabledClick && onDisabledClick(e as any) : onClick && onClick(e as any))}
			{...(rest as any)}
		>
			{children}
		</Elem>
	)
}

export function getSizeClassnames<T extends NonNullable<BaseProps['size']>>(size: T | undefined) {
	switch (size) {
		case 'xs':
			return 'text-base font-bold px-5 py-1 tracking-none'
		case 'sm':
			return 'text-sm font-bold px-6 py-3 tracking-[1.5px]'
		case 'md':
			return 'text-xl px-10 py-1.5 font-semibold'
		case 'lg':
			return 'text-xl'
		case 'xl':
			return 'text-xl tracking-[1.5px] font-bold p-5'
	}
	return 'text-base'
}

export default Button
